import { useState, useEffect, useCallback } from 'react';
import { X, Gift, Sparkles, ArrowRight, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import CouponBanner from './CouponBanner';
import CountdownTimer from './CountdownTimer';
import { usePromotional } from '../../contexts/PromotionalContext';
import { PROMOTED_COURSES, ROTATION_INTERVAL } from '../../config/promotedCourses';
import { cn } from '@/utils/cn';

interface PromotionalBannerProps {
  variant?: 'hero' | 'sticky' | 'inline';
  className?: string;
  dismissible?: boolean;
  onDismiss?: () => void;
}

const PromotionalBanner = ({ 
  variant = 'hero',
  className,
  dismissible = false,
  onDismiss 
}: PromotionalBannerProps) => {
  const { config, isActive } = usePromotional();
  const [isDismissed, setIsDismissed] = useState(false);
  const [currentCourseIndex, setCurrentCourseIndex] = useState(0);
  const [isCourseTransitioning, setIsCourseTransitioning] = useState(false);

  const totalSlideCount = 1 + PROMOTED_COURSES.length;

  const goToNextCourse = useCallback(() => {
    if (totalSlideCount <= 1) return;
    setIsCourseTransitioning(true);
    setTimeout(() => {
      setCurrentCourseIndex((prev) => (prev + 1) % totalSlideCount);
      setIsCourseTransitioning(false);
    }, 300);
  }, [totalSlideCount]);

  const goToPrevCourse = useCallback(() => {
    if (totalSlideCount <= 1) return;
    setIsCourseTransitioning(true);
    setTimeout(() => {
      setCurrentCourseIndex((prev) => (prev - 1 + totalSlideCount) % totalSlideCount);
      setIsCourseTransitioning(false);
    }, 300);
  }, [totalSlideCount]);

  useEffect(() => {
    if (totalSlideCount <= 1) return;
    const timer = setInterval(goToNextCourse, ROTATION_INTERVAL);
    return () => clearInterval(timer);
  }, [goToNextCourse, totalSlideCount]);

  const handleDismiss = () => {
    setIsDismissed(true);
    onDismiss?.();
  };

  // Don't render if promotion is not enabled, not active, or component is dismissed
  if (!config.isEnabled || !isActive || isDismissed) {
    return null;
  }

  // Check visibility settings for different variants
  if (variant === 'hero' && !config.showHeroBanner) return null;
  if (variant === 'sticky' && !config.showStickyHeader) return null;
  if (variant === 'inline' && !config.showInlinePromotions) return null;

  const getVariantStyles = () => {
    switch (variant) {
      case 'hero':
        return 'bg-gradient-to-r from-orange-500 via-red-500 to-pink-600 text-white py-6 px-6 rounded-2xl shadow-2xl border-2 border-orange-300/50';
      case 'sticky':
        return 'bg-gradient-to-r from-orange-500 to-red-600 text-white py-3 px-4 shadow-lg border-b-2 border-orange-300/50';
      case 'inline':
        return 'bg-gradient-to-r from-orange-50 to-red-50 border-2 border-orange-200 text-orange-900 py-4 px-4 rounded-xl';
      default:
        return 'bg-gradient-to-r from-orange-500 via-red-500 to-pink-600 text-white py-6 px-6 rounded-2xl shadow-2xl';
    }
  };

  if (variant === 'sticky') {
    return (
      <div className={cn(
        'fixed top-0 left-0 right-0 z-50 animate-slide-down',
        getVariantStyles(),
        className
      )}>
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-shrink-0">
              <Gift className="h-5 w-5 animate-bounce" aria-label="Gift icon" />
              <Sparkles className="h-4 w-4 animate-pulse" aria-label="Sparkles icon" />
            </div>
            <div className="min-w-0 flex-1">
              <span className="text-sm sm:text-base font-bold">
                {config.urgencyMessage}
              </span>
              <span className="hidden sm:inline text-sm ml-2 opacity-90">
                Use code {config.couponCode}
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-3 flex-shrink-0">
            <CountdownTimer 
              endDate={config.endDate}
              variant="compact"
              className="text-white text-xs"
            />
            <Link
              to="/courses"
              className="bg-white text-orange-600 px-3 py-1.5 rounded-full text-sm font-semibold hover:bg-orange-50 transition-all transform hover:scale-105"
            >
              Get Now
            </Link>
            {dismissible && (
              <button
                onClick={handleDismiss}
                className="p-1 hover:bg-white/20 rounded-full transition-all"
              >
                <X className="h-4 w-4" aria-label="X icon" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <div className={cn(
        'relative overflow-hidden animate-pulse-glow',
        getVariantStyles(),
        className
      )}>
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-32 h-32 bg-orange-300/20 rounded-full blur-xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-24 h-24 bg-red-300/20 rounded-full blur-xl animate-pulse delay-500"></div>
        </div>
        
        <div className="relative">
          <div className="flex items-center justify-between gap-4 mb-3">
            <div className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-orange-600 animate-bounce" aria-label="Gift icon" />
              <span className="text-lg font-bold text-orange-800">Limited Time Offer!</span>
            </div>
            {dismissible && (
              <button
                onClick={handleDismiss}
                className="p-1 hover:bg-orange-200 rounded-full transition-all"
              >
                <X className="h-4 w-4 text-orange-600" aria-label="X icon" />
              </button>
            )}
          </div>
          
          <div className="space-y-3">
            <CouponBanner
              code={config.couponCode}
              description={config.description}
              variant="card"
              className="border-orange-300"
            />
            
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <CountdownTimer 
                endDate={config.endDate}
                variant="detailed"
                className="text-orange-800"
              />
              
              <Link
                to="/courses"
                className="bg-gradient-to-r from-orange-600 to-red-600 text-white px-6 py-2 rounded-full font-semibold hover:from-orange-700 hover:to-red-700 transition-all transform hover:scale-105 shadow-lg flex items-center justify-center gap-2"
              >
                Claim Offer
                <ArrowRight className="h-4 w-4" aria-label="ArrowRight icon" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Total slides: 1 promo slide + N course slides
  const totalSlides = 1 + PROMOTED_COURSES.length;

  // Hero variant (default)
  return (
    <div className={cn(
      'relative overflow-hidden animate-pulse-glow',
      getVariantStyles(),
      className
    )}>
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-40 h-40 bg-orange-300/20 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-32 h-32 bg-pink-300/20 rounded-full blur-xl animate-pulse delay-500"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-28 h-28 bg-red-300/20 rounded-full blur-xl animate-pulse delay-1000"></div>
      </div>
      
      <div className="relative">
        {dismissible && (
          <button
            onClick={handleDismiss}
            className="absolute top-0 right-0 p-2 hover:bg-white/20 rounded-full transition-all"
          >
            <X className="h-5 w-5" aria-label="X icon" />
          </button>
        )}
        
        <div className="text-center space-y-4">
          {/* Navigation arrows */}
          <div className="flex items-center justify-center gap-4">
            {totalSlides > 1 && (
              <button onClick={goToPrevCourse} className="p-2 hover:bg-white/20 rounded-full transition-all flex-shrink-0" aria-label="Previous slide">
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}

            {/* Slide content with transition */}
            <div className={`flex-1 transition-opacity duration-300 ${isCourseTransitioning ? 'opacity-0' : 'opacity-100'}`}>
              {currentCourseIndex === 0 ? (
                /* Slide 0: Promotional / Discount */
                <div className="space-y-4">
                  <div className="flex items-center justify-center gap-3">
                    <Gift className="h-8 w-8 animate-bounce" aria-label="Gift icon" />
                    <Sparkles className="h-6 w-6 animate-spin-slow" aria-label="Sparkles icon" />
                    <span className="text-2xl sm:text-3xl font-bold tracking-tight">
                      {config.urgencyMessage}
                    </span>
                    <Sparkles className="h-6 w-6 animate-spin-slow" aria-label="Sparkles icon" />
                    <Gift className="h-8 w-8 animate-bounce" aria-label="Gift icon" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold">
                    MASSIVE {config.discountPercentage}% OFF All AI Testing Courses!
                  </h3>
                  <p className="text-base sm:text-lg opacity-90 max-w-2xl mx-auto">
                    Master AI-powered testing with our complete learning path. From GenAI fundamentals to advanced multi-agent systems.
                  </p>
                  <div className="flex items-center justify-center -space-x-5 py-1" aria-label="Featured AI courses">
                    {PROMOTED_COURSES.map((course, index) => (
                      <div
                        key={course.id}
                        className="relative w-28 sm:w-36 aspect-video rounded-lg overflow-hidden border-2 border-white/80 shadow-xl bg-white/10 transition-transform duration-300 hover:-translate-y-1 hover:z-10"
                        style={{ transform: `rotate(${(index - 1) * 3}deg)` }}
                      >
                        <img
                          src={course.image}
                          alt={`${course.shortTitle} course banner`}
                          loading="eager"
                          decoding="async"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-col lg:flex-row items-center justify-center gap-4 lg:gap-8">
                    <CouponBanner
                      code={config.couponCode}
                      description={config.description}
                      variant="banner"
                      className="bg-white/20 border-2 border-white/30"
                    />
                    <CountdownTimer 
                      endDate={config.endDate}
                      variant="hero"
                      className="text-white"
                    />
                  </div>
                  <div className="pt-2">
                    <Link
                      to="/courses"
                      className="inline-flex items-center gap-2 bg-white text-orange-600 px-8 py-3 rounded-full text-lg font-bold hover:bg-orange-50 transition-all transform hover:scale-105 shadow-xl"
                    >
                      Claim Your {config.discountPercentage}% Discount
                      <ArrowRight className="h-5 w-5" aria-label="ArrowRight icon" />
                    </Link>
                  </div>
                </div>
              ) : (
                /* Slide 1+: Course slides */
                (() => {
                  const course = PROMOTED_COURSES[currentCourseIndex - 1];
                  return (
                    <div className="space-y-4">
                      <div className="flex items-center justify-center gap-3">
                        <Sparkles className="h-6 w-6 text-yellow-300 animate-pulse" aria-label="Sparkles icon" />
                        <span className="text-2xl sm:text-3xl font-bold tracking-tight">
                          🔥 LEVEL UP SKILL OFFER! 🔥
                        </span>
                        <Sparkles className="h-6 w-6 text-yellow-300 animate-pulse" aria-label="Sparkles icon" />
                      </div>
                      <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 max-w-3xl mx-auto">
                        <a
                          href={course.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group/image relative w-44 sm:w-56 aspect-video rounded-xl overflow-hidden border-2 border-white/80 shadow-2xl bg-white/10 flex-shrink-0"
                        >
                          <img
                            src={course.image}
                            alt={`${course.title} course banner`}
                            loading="eager"
                            decoding="async"
                            className="w-full h-full object-cover transition-transform duration-500 group-hover/image:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent"></div>
                        </a>

                        <div className="min-w-0 text-center sm:text-left">
                          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/20 backdrop-blur-sm mb-3">
                            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
                            <span className="text-white text-sm font-bold uppercase tracking-wider">{course.badge}</span>
                          </div>
                          <h3 className="text-xl sm:text-2xl font-bold mb-2">{course.shortTitle}</h3>
                          <p className="text-sm sm:text-base opacity-90 mb-4">
                            {course.description}
                          </p>
                          <a
                            href={course.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 bg-white text-indigo-600 px-6 py-2.5 rounded-full text-base font-bold hover:bg-yellow-100 transition-all transform hover:scale-105 shadow-xl"
                          >
                            Enroll Now
                            <ExternalLink className="w-4 h-4" aria-label="ExternalLink icon" />
                          </a>
                        </div>
                      </div>
                    </div>
                  );
                })()
              )}
            </div>

            {totalSlides > 1 && (
              <button onClick={goToNextCourse} className="p-2 hover:bg-white/20 rounded-full transition-all flex-shrink-0" aria-label="Next slide">
                <ChevronRight className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Slide indicators */}
          {totalSlides > 1 && (
            <div className="flex justify-center gap-1.5 pt-2">
              {Array.from({ length: totalSlides }).map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => { setIsCourseTransitioning(true); setTimeout(() => { setCurrentCourseIndex(idx); setIsCourseTransitioning(false); }, 300); }}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${idx === currentCourseIndex ? 'bg-white w-4' : 'bg-white/50'}`}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PromotionalBanner;
